import z from "zod"

const maxDecimals = (val) => {
    if (val === undefined || val === null || val === "") return true;
    const strVal = val.toString();
    const decimal = strVal.split('.')[1];
    return !decimal || decimal.length <= 2;
}

const currencyMsg = (name) => ({message: `El campo ${name} solo permite 2 decimales`});
const mandatoryMsg = (name) => ({message: `El campo ${name} es obligatorio`});

const exclusions = {
    rollWidth: [0,0,1],
    additionalSub: [1,0,1],
    across: [0,0,1],
    around: [0,0,1],
    labelsAcross: [0,0,1],
    labelsXRoll: [0,0,1],
    rewindOrientation: [0,0,1],
    glue: [1,0,0],
    coatingLine: [0,0,1],
    pages: [0, 1, 1],
    frontPage: [0, 1, 1],
    interior: [0, 1, 1],
    sleeves: [0, 0, 1],
    rewindType: [0, 0, 1],
    piecesXSleeve: [1, 1, 0],
    piecesXBox: [1, 1, 0],
    casesXPallet: [1, 1, 0]
    
}

const formatIndex = {
    "Caja": 0, "Plana": 1, "Flexo": 2
}

export const budgetSchema = z.object ({
    // --------- Metadata ---------
    status: z.string().default("Borrador").optional(),
    id: z.string().optional(),
    checkedAt: z.string().optional(),
    approved: z.boolean().default(false).optional(),
    requestedApproval: z.boolean().default(false).optional(),
    approvalDate: z.union([z.coerce.date(), z.literal(""), z.literal(null)]).nullable().optional(),

    // --------- CustomerAndRequest ---------

    // ***** Checkbox *****
    requirements: z.boolean().default(false).optional(),
    certMatPr: z.boolean().default(false).optional(),
    certProdTerm: z.boolean().default(false).optional(),
    newProc: z.boolean().default(false).optional(),
    newMat: z.boolean().default(false).optional(),

    original: z.string().min(1, mandatoryMsg("Original")),   //Dropwdown (single selection)
    title: z.string().min(1, mandatoryMsg("Título")),
    format: z.string().min(1, mandatoryMsg("Formato")).default("Caja"), //Dropwdown (single selection)
    controlNum: z.string().regex(/^\d{4,5}$/, "Control debe tener entre 4 y 5 dígitos"),
    partNum: z.string().min(1, mandatoryMsg("No. Parte")),
    urgency: z.string().optional(), //Dropwdown (single selection)

    customerSearch: z.string().min(1, mandatoryMsg("Cliente a facturar")),
    remunerationGroup: z.string().min(1, mandatoryMsg("Grupo de remuneración")),  //Dropwdown (single selection)
    deliverTo: z.string().optional(),
    atn: z.string().optional(),
    phone: z.string().optional().refine((val) => !val || val.replace(/\D/g, '').length >= 10, {
        message: "El teléfono debe tener al menos 10 dígitos"
    }),
    mail: z.email({message: "Correo inválido"}).optional(),

    package: z.string().min(1, mandatoryMsg("Empaque")).refine(maxDecimals, currencyMsg("Empaque")),
    freight: z.string().min(1, mandatoryMsg("Flete")).refine(maxDecimals, currencyMsg("Flete")),
    customerAccepts: z.string().min(1, mandatoryMsg("Cliente acepta")),    //Dropwdown (single selection)
    piecesXSleeve: z.coerce.number().int().optional(),  //This is mandatory!
    piecesXBox: z.coerce.number().int().optional(), //This is mandatory!
    casesXPallet: z.coerce.number().int().optional(),   //This is mandatory!

    rebase: z.boolean().default(false),
    solid: z.boolean().default(false),
    backup: z.boolean().default(false),
    colorGamut: z.boolean().default(false),

    fileFormat: z.string().optional(),
    sheet: z.string().min(1, mandatoryMsg("Pliego")),
    printSize: z.string().min(1, mandatoryMsg("Tamaño de impresión")),
    gangRun: z.string().min(1, mandatoryMsg("Planilla")),


    // --------- TechSpecs ---------
    // ------ DimensionAndTolerance ------
    flatSize: z.string().min(1, mandatoryMsg("Medida extendida")),
    finalSize: z.string().min(1, mandatoryMsg("Medida final")),
    chain: z.string().optional(),   //Dropdown (single selection)
    flute: z.string().optional(),   //Dropdown (single selection)
    front: z.string().optional(),
    side: z.string().optional(),
    height: z.string().optional(),
    tolerance: z.string().min(1, mandatoryMsg("Tolerancia")),

    // ------ SubstratumAndInk ------
    substrates: z.array(z.object({substrate: z.string().min(1, mandatoryMsg("Sustrato")), grammage: z.string().min(1, mandatoryMsg("Gramaje")), rollWidth: z.string()})).max(3, {
        message: "No se permiten más de 3 sustratos"
    }).default([]),
    frontInk: z.string().min(1, mandatoryMsg("Tintas frente")),
    backInk: z.string().min(1, mandatoryMsg("Tintas vuelta")),
    pages: z.string().optional(),
    frontPage: z.string().optional(),
    interior: z.string().optional(),

    // ------ ProductiveProcess ------
    activeProcesses: z.array(z.string()).default([]), //This array will store the keys of the selected options
    
    // ------ ProductionParams ------
    inkMixTxt: z.string().optional(),
    press: z.string().min(1, mandatoryMsg("Prensa")),  //Dropdown (single selection)
    clickChargeTxt1: z.string().optional(),
    clickChargeTxt2: z.string().optional(),
    additionalSub: z.string().optional(),
    chemicalsTxt: z.string().optional(),
    splice: z.string().min(1, mandatoryMsg("Empalme")),
    whiteUnderprint: z.string().min(1, mandatoryMsg("Cama de blanco")),    //Dropdown (single selection)
    coating: z.object({line: z.array(z.string()), application: z.array(z.string()), faces: z.array(z.string())}),    //Dropdown (multiple selection)
    sleeves: z.string().optional(), //Dropdown (single selection). This is mandatory!
    tradePrint: z.string().min(1, mandatoryMsg("Maquila")),
    across: z.string().optional(),
    around: z.string().optional(),
    design: z.string().optional(),
    ctpTxt: z.string().optional(),
    cutTxt: z.string().optional(),
    shapeCutTxt: z.string().optional(),
    positiveTxt: z.string().optional(),

    // ------ Finishes ------
    activeFinishes: z.array(z.string()).default([]), //This array will store the keys of the selected options

    // ------ Finishes ------
    dieCut: z.string().min(1, mandatoryMsg("Suaje")),  //Dropdown (single selection)
    tool: z.string().min(1, mandatoryMsg("Herramienta")),
    glue: z.string().optional(),  //Dropdown (single selection)
    glueFlap: z.string().optional(),
    coatingFinish: z.object({line: z.array(z.string()), application: z.array(z.string()), faces: z.array(z.string())}),   //Dropdown (multiple selection)
    laminationTxt: z.object({type: z.array(z.string()), application: z.array(z.string()), faces: z.array(z.string())}),  //Dropdown (multiple selection)
    rewindType: z.string().optional(),    //Dropdown (single selection). This is mandatory!
    labelsAcross: z.coerce.number().int().optional(),
    labelsXRoll: z.coerce.number().int().optional(),
    rewindOrientation: z.coerce.number().int().default(0).optional(), 


    // --------- PricesAndControl ---------
    paper: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Papel")),
    plates: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Placas")),
    toolCost: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Costo herramienta")),
    cost: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Costo")),
    secondProc: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Segundo proceso")),
    opProc: z.coerce.number().optional().refine(maxDecimals, currencyMsg("Proceso opcional")),

    costXQuantity: z.array(z.object({quantity: z.coerce.number(), price: z.coerce.number()})).default([]).optional().refine((arr) => !arr || arr.length <= 6, {
        message: "No se permiten más de 6 precios"
    }),

    authQuantity: z.coerce.number().int().optional(),
    unitPrice: z.coerce.number().optional(),
    dieCutPrice: z.coerce.number().optional(),
    urgencyPrice: z.coerce.number().optional(),
    total: z.coerce.number().optional(),

    budget: z.coerce.number().int().optional(),
    control: z.coerce.number().optional(),
    deposit: z.coerce.number().optional(),
    invoice: z.string().optional(),
    order: z.string().optional(),
    startDate: z.coerce.date().nullable().optional(),
    dueDate: z.coerce.date().nullable().optional(),
    lastShip: z.coerce.date().nullable().optional(),
    attended: z.string().optional(),
    cotized: z.string().optional(),
    submitted: z.string().optional(),
    checked: z.string().optional(),

    customerNotes: z.string().optional(),
    techNotes: z.string().optional(),
    pricesNotes: z.string().optional()
}).superRefine((data, ctx) => {
    const currentDay = new Date();
    currentDay.setHours(0, 0, 0, 0);

    if (data.startDate && data.startDate < currentDay) {
        ctx.addIssue({code: "custom", message: "La fecha de ingreso no puede ser anterior a la fecha actual", path: ["startDate"]});
    }

    if (data.startDate && data.dueDate && data.dueDate < data.startDate) {
        ctx.addIssue({code: "custom", message: "La fecha de entrega no puede ser anterior a la fecha de ingreso", path: ["dueDate"]});
    }

    if (data.dueDate && data.lastShip) {
        const diffDays = (data.lastShip - data.dueDate) / (1000*60*60*24);
        if (diffDays > 2) {
            ctx.addIssue({code: "custom", message: "La diferencia entre la entrega y el embarque no puede ser mayor a dos días"})
        }

        if (diffDays < 1) {
            ctx.addIssue({code: "custom", message: "La diferencia entre la entrega y el embarque no puede ser menor a un día"}) 
        }
    }

    const idx = formatIndex[data.format];
    
    const validateRequired = (conditionArr, value, path, label) => {
        if (idx === undefined) return;
        const isReq = conditionArr[idx] === 1;
        const isEmpty = value === undefined || value === null || value === "" || value === 0;

        if (isReq && isEmpty) {
            ctx.addIssue({code: "custom", ...mandatoryMsg(label), path: Array.isArray(path) ? path: [path]})
        }
    }

    //Special validations. These are required to avoid errors from not rendered fields.
    validateRequired(exclusions.additionalSub, data.additionalSub, "additionalSub", "Sustrato adicional");
    validateRequired(exclusions.across, data.across, "across", "Figuras al paso");
    validateRequired(exclusions.around, data.around, "around", "Figuras alrededor");
    validateRequired(exclusions.glue, data.glue, "glue", "Pegado");
    validateRequired(exclusions.labelsAcross, data.labelsAcross, "labelsAcross", "Etiquetas al paso");
    validateRequired(exclusions.labelsXRoll, data.labelsXRoll, "labelsXRoll", "Etiquetas por rollo");
    validateRequired(exclusions.rewindOrientation, data.rewindOrientation, "rewindOrientation", "Orientación");
    validateRequired(exclusions.pages, data.pages, "pages", "Páginas");
    validateRequired(exclusions.frontPage, data.frontPage, "frontPage", "Portada");
    validateRequired(exclusions.interior, data.interior, "interior", "Interior");
    validateRequired(exclusions.sleeves, data.sleeves, "sleeves", "Mangas");
    validateRequired(exclusions.rewindType, data.rewindType, "rewindType", "Tipo de rebobinado");
    validateRequired(exclusions.piecesXSleeve, data.piecesXSleeve, "piecesXSleeve", "Piezas por fajilla");
    validateRequired(exclusions.piecesXBox, data.piecesXBox, "piecesXBox", "Piezas por caja");
    validateRequired(exclusions.casesXPallet, data.casesXPallet, "casesXPallet", "Cajas por tarima");

    if (exclusions.coatingLine[idx] === 1 && (!data.coating.line || data.coating.line.length === 0)) {
        ctx.addIssue({code: "custom", ...mandatoryMsg("Barniz en línea"), path: ["coating", "line"]})
    }

    if (exclusions.rollWidth[idx] === 1) {
        data.substrates.forEach((sub, i) => {
            if (!sub.rollWidth) {
                ctx.addIssue({code: "custom", ...mandatoryMsg(`Ancho de bobina (Sustrato ${i + 1})`), path: ["substrates", i, "rollWidth"]});
            }
        })
    }
}) 